import React from "react";
import Table from "../../../core/common/dataTable/index";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

interface LeaveReportDetailsProps {
  data: any[];
  onBack: () => void;
}

const LeaveReportDetails: React.FC<LeaveReportDetailsProps> = ({
  data,
  onBack,
}) => {
  // Detect if it's leave application data (has "from_date") or leave balance data (has "opening_balance")
  const isLeaveApplicationData = data.length && "from_date" in data[0];

  const columns: any[] = [];

  if (isLeaveApplicationData) {
    // Leave Application Data Columns
    const statuses = Array.from(
      new Set(data.map((item) => item.status?.toUpperCase()))
    );

    columns.push(
      { title: "EmpCode", dataIndex: "employee_id" },
      { title: "EmployeeName", dataIndex: "employee_name" },
      { title: "Email", dataIndex: "email_id" },
      { title: "LeaveType", dataIndex: "leave_type" },
      {
        title: "FromDate",
        dataIndex: "from_date",
        render: (value: string) =>
          value ? dayjs(value).format("D MMMM YYYY") : "-",
      },
      {
        title: "ToDate",
        dataIndex: "to_date",
        render: (value: string) =>
          value ? dayjs(value).format("D MMMM YYYY") : "-",
      },
      { title: "LeaveSlice", dataIndex: "leave_slice_type" },
      {
        title: "ApplicationDate",
        dataIndex: "application_date",
        render: (value: string) =>
          value ? dayjs(value).format("D MMMM YYYY") : "-",
      },
      { title: "ManagerEmail", dataIndex: "manager_email_id" },
      {
        title: "Status",
        dataIndex: "status",
        render: (text: string) => {
          let color = "";
          switch (text?.toLowerCase()) {
            case "pending":
              color = "warning";
              break;
            case "approved":
              color = "success";
              break;
            case "rejected":
              color = "danger";
              break;
            default:
              color = "secondary";
          }
          return (
            <span
              className={`badge bg-${color}`}
              style={{ textTransform: "capitalize" }}
            >
              {text || "-"}
            </span>
          );
        },
      }
    );

    if (statuses.includes("APPROVED")) {
      columns.push(
        {
          title: "ApprovedBy",
          render: (_: any, record: any) =>
            record.approved_by_name || record.approved_by || "-",
        },
        {
          title: "ApprovedOn",
          dataIndex: "approved_on",
          render: (value: string) =>
            value ? dayjs(value).format("D MMMM YYYY") : "-",
        },
      );
    }

    if (statuses.includes("REJECTED")) {
      columns.push(
        {
          title: "RejectedBy",
          render: (_: any, record: any) =>
            record.rejected_by_name || record.rejected_by || "-",
        },
        {
          title: "RejectedOn",
          dataIndex: "rejected_on",
          render: (value: string) =>
            value ? dayjs(value).format("D MMMM YYYY") : "-",
        },
        {
          title: "RejectionReason",
          dataIndex: "rejection_reason",
        }
      );
    }
  } else {
    // Leave Balance Data Columns
    columns.push(
      { title: "EmpCode", dataIndex: "employee_id" },
      { title: "EmployeeName", dataIndex: "employee_name" },
      { title: "Email", dataIndex: "email_id" },
      { title: "LeaveType", dataIndex: "leave_type" },
      { title: "OpeningBalance", dataIndex: "opening_balance" },
      { title: "EntitlementBalance", dataIndex: "entitlement_balance" },
      { title: "ProRataBalance", dataIndex: "prorata_balance" },
      { title: "AvailedBalance", dataIndex: "availed_balance" },
      { title: "ClosingBalance", dataIndex: "closing_balance" },
    );
  }

  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    const isLeaveApplication = "from_date" in data[0];

    const employeeName = data[0]?.employee_name?.replace(/\s+/g, "_") || "Employee";
    const fileNamePrefix = isLeaveApplication ? "Leave_Ledger" : "Leave_Balance";
    const fileName = `${fileNamePrefix}_${employeeName}.xlsx`;

    const formattedData = data.map((item) => {
      if (isLeaveApplication) {
        const row: any = {
          EmpCode: item.employee_id,
          EmployeeName: item.employee_name,
          Email: item.email_id,
          LeaveType: item.leave_type,
          FromDate: item.from_date,
          ToDate: item.to_date,
          LeaveSlice: item.leave_slice_type,
          ApplicationDate: item.application_date,
          Reason: item.application_reason,
          ManagerEmail: item.manager_email_id,
          Status: item.status,
        };

        if (item.status?.toUpperCase() === "APPROVED") {
          row["ApprovedBy"] = item.approved_by_name || item.approved_by || "-";
          row["ApprovedOn"] = item.approved_on
            ? item.approved_on.split("T")[0]
            : "-";
        }

        if (item.status?.toUpperCase() === "REJECTED") {
          row["RejectedBy"] = item.rejected_by_name || item.rejected_by || "-";
          row["RejectedOn"] = item.rejected_on
            ? item.rejected_on.split("T")[0]
            : "-";
          row["RejectionReason"] = item.rejection_reason || "-";
        }

        return row;
      } else {
        return {
          EmpCode: item.employee_id,
          EmployeeName: item.employee_name,
          Email: item.email_id,
          LeaveType: item.leave_type,
          OpeningBalance: item.opening_balance,
          EntitlementBalance: item.entitlement_balance,
          ProRataBalance: item.prorata_balance,
          AvailedBalance: item.availed_balance,
          ClosingBalance: item.closing_balance,
        };
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Report");
    XLSX.writeFile(workbook, fileName);
  };


  return (
    <div className="card mt-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Leave Report Details</h5>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary custom_button"
            onClick={onBack}
          >
            Back
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={exportToExcel}
          >
            Download Excel
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        <Table dataSource={data} columns={columns} Selection={false} />
      </div>
    </div>
  );
};

export default LeaveReportDetails;
